import compact from 'lodash/compact';

import Lesson from '@/models/Lesson';
import LessonProgressDashboardStats, {
  BookProgressDashboard,
} from '@/models/LessonProgressDashboardStats';

export type LessonItemType =
  | 'instruction'
  | 'activity'
  | 'material'
  | 'teacherBook'
  | 'challenge'
  | 'resources';

export type StudentLessonItemType = Exclude<LessonItemType, 'teacherBook'>;

export type LessonItemStatus = 'done' | 'pending' | 'neutral';

export type LessonItem = {
  type: LessonItemType;
  status: LessonItemStatus;
  pendingCount?: number;
  progress?: number;
};

export type StudentLessonItem = LessonItem & {
  type: StudentLessonItemType;
};

const toDoneStatus = (done: boolean): LessonItemStatus =>
  done ? 'done' : 'neutral';

const toBookStatus = ({
  progress,
}: BookProgressDashboard): Pick<LessonItem, 'status' | 'progress'> => {
  if (progress >= 1) return { status: 'done' };
  if (progress > 0) return { status: 'pending', progress };

  return { status: 'neutral' };
};

const toPendingStatus = (
  started: boolean,
  pendingCount: number,
): Pick<LessonItem, 'status' | 'pendingCount'> => {
  if (pendingCount === 0) return { status: 'done' };
  if (!started) return { status: 'neutral' };

  return { status: 'pending', pendingCount };
};

export function toStudentLessonItems(
  progress: LessonProgressDashboardStats,
): StudentLessonItem[] {
  const {
    hasAccessedTool,
    hasWatchedInstruction,
    bookProgress,
    activitiesProgress,
    challengeProgress,
    resources,
  } = progress;

  const started = progress.progress > 0;
  const instructionFlags = [hasAccessedTool, hasWatchedInstruction];
  const hasInstruction = instructionFlags.some(flag => flag !== null);
  const pendingActivities = activitiesProgress.filter(
    activity => !activity.hasDone,
  ).length;
  const pendingResources = resources.filter(
    resource => !resource.hasOpened,
  ).length;

  return compact<StudentLessonItem>([
    hasInstruction && {
      type: 'instruction',
      status: toDoneStatus(instructionFlags.every(flag => flag ?? true)),
    },
    activitiesProgress.length > 0 && {
      type: 'activity',
      ...toPendingStatus(started, pendingActivities),
    },
    bookProgress && {
      type: 'material',
      ...toBookStatus(bookProgress),
    },
    challengeProgress && {
      type: 'challenge',
      ...toPendingStatus(started, challengeProgress.hasDone ? 0 : 1),
    },
    resources.length > 0 && {
      type: 'resources',
      ...toPendingStatus(started, pendingResources),
    },
  ]);
}

export function toStaffLessonItems({
  instructionsUrl,
  toolUrl,
  activities,
  book,
  teacherBook,
  challenge,
  resources,
}: Lesson): LessonItem[] {
  return compact<LessonItem>([
    !!(instructionsUrl || toolUrl) && {
      type: 'instruction',
      status: 'neutral',
    },
    activities.length > 0 && { type: 'activity', status: 'neutral' },
    !!book && { type: 'material', status: 'neutral' },
    !!teacherBook && { type: 'teacherBook', status: 'neutral' },
    !!challenge && { type: 'challenge', status: 'neutral' },
    resources.length > 0 && { type: 'resources', status: 'neutral' },
  ]);
}
